import { useState } from 'react';

import { AppIcon, type AppIcons, InfoModal } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import type { MemberAssessments } from '@backrs_ui/grow-ui/src/redux/coachingDashboard';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';

const {
  modules: {
    coachingDashboard: { assessmentsCell },
  },
} = appStrings;

/** The three assessments, in the order they are always presented. */
const ASSESSMENTS = [
  {
    key: 'blueprint',
    name: assessmentsCell.blueprint,
    note: assessmentsCell.blueprintNote,
    icon: 'Map01' as AppIcons,
    completed: (assessments?: MemberAssessments) => assessments?.blueprint?.completed === true,
  },
  {
    key: 'track_assessment',
    name: assessmentsCell.trackAssessment,
    note: assessmentsCell.trackAssessmentNote,
    icon: 'Route' as AppIcons,
    completed: (assessments?: MemberAssessments) =>
      assessments?.track_assessment?.completed === true,
  },
  {
    key: 'life_readiness',
    name: assessmentsCell.lifeReadiness,
    note: assessmentsCell.lifeReadinessNote,
    icon: 'Speedometer03' as AppIcons,
    completed: (assessments?: MemberAssessments) => assessments?.life_readiness?.completed === true,
  },
] as const;

type MemberAssessmentsCellProps = Readonly<{
  assessments?: MemberAssessments;
}>;

export const MemberAssessmentsCell = ({ assessments }: MemberAssessmentsCellProps) => {
  const completed = ASSESSMENTS.filter((assessment) => assessment.completed(assessments)).length;

  return (
    <Typography variant="text-sm-regular" sx={summarySx}>
      {assessmentsCell.summary(completed, ASSESSMENTS.length)}
    </Typography>
  );
};

const summarySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  whiteSpace: 'nowrap',
});

const noteSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
};

/** What each of the three assessments means. */
export const MemberAssessmentsHeaderInfo = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box
        id="coaching-dashboard-assessments-info"
        component="span"
        sx={noteSx}
        onClick={() => setOpen(true)}
      >
        <AppIcon iconName="InfoCircle" diameter="14px" color="Gray/500" />
      </Box>
      <InfoModal
        open={open}
        onClose={() => setOpen(false)}
        dialogSx={infoDialogSx}
        icon="AnnotationInfo"
        hideCircleImage
        headerContent={
          <Typography variant="text-lg-semibold" sx={titleSx}>
            {assessmentsCell.infoTitle}
          </Typography>
        }
        bodyContent={
          <Box sx={cardListSx}>
            {ASSESSMENTS.map(({ key, name, note, icon }) => (
              <Box key={key} sx={cardSx}>
                <Box sx={cardHeaderSx}>
                  <Box sx={cardIconSx}>
                    <AppIcon iconName={icon} diameter="20px" color="Colors/Base/White" />
                  </Box>
                  <Typography variant="text-lg-semibold" sx={secondaryTextSx}>
                    {name}
                  </Typography>
                </Box>
                <Typography variant="text-sm-regular" sx={tertiaryTextSx}>
                  {note}
                </Typography>
              </Box>
            ))}
          </Box>
        }
      />
    </>
  );
};

const infoDialogSx: SxProps<Theme> = {
  '& .MuiDialog-paper': { maxWidth: '500px' },
};

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/900'],
});

const secondaryTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const tertiaryTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const cardListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  padding: backrs.spacing['spacing-lg'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const cardHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});

const cardIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  borderRadius: backrs.radius['radius-full'],
  backgroundColor: backrs.colors['Brand/500'],
  flexShrink: 0,
});
